
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useCallback } from "react";
import { getDashboardByRole } from "@/utils/routeUtils";
import type { AuthUser, AuthError } from "@/types/auth";

export const useTopBarAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userTier, setUserTier] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const fetchUserRole = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("role, user_tier")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error fetching user role:", error);
        setError({ message: "Failed to fetch user role" });
        return;
      }

      setUserRole(data?.role || null);
      setUserTier(data?.user_tier || null);
      setError(null);
    } catch (error) {
      console.error("Error fetching user role:", error);
      setError({ message: "Failed to fetch user role" });
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          setError({ message: sessionError.message });
          return;
        }

        if (mounted) {
          setUser(session?.user ?? null);
          if (session?.user) {
            await fetchUserRole(session.user.id);
          }
        }

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('Auth state changed:', event);
            
            if (!mounted) return;

            if (event === 'SIGNED_OUT') {
              setUser(null);
              setUserRole(null);
              setUserTier(null);
              setError(null);
              navigate('/login');
              return;
            }

            if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
              setUser(session.user);
              await fetchUserRole(session.user.id);
              
              // Redirect to appropriate dashboard after login
              if (event === 'SIGNED_IN' && location.pathname === '/login') {
                try {
                  const { data, error } = await supabase
                    .from("profiles")
                    .select("role, user_tier")
                    .eq("id", session.user.id)
                    .single();

                  if (!error && data) {
                    const dashboardPath = getDashboardByRole(data.role, data.user_tier);
                    navigate(dashboardPath);
                  }
                } catch (redirectError) {
                  console.error('Redirect error:', redirectError);
                  setError({ message: 'Failed to redirect after login' });
                }
              }
            }
          }
        );

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setUser(null);
          setUserRole(null);
          setUserTier(null);
          setError({ message: 'Authentication initialization failed' });
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, [navigate, location.pathname, fetchUserRole]);

  const handleLogout = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Clear local state first
      setUser(null);
      setUserRole(null);
      setUserTier(null);
      
      // Clear any stored return paths
      localStorage.removeItem('returnTo');
      
      // Attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Logout error:", error);
        toast({
          title: "Signed out",
          description: "You have been signed out.",
        });
      } else {
        toast({
          title: "Signed out successfully",
          description: "You have been logged out.",
        });
      }
      
      // Always navigate to home page
      navigate("/");
      
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Signed out",
        description: "You have been signed out.",
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  const getDashboardLink = useCallback(() => {
    return getDashboardByRole(userRole || '', userTier || '');
  }, [userRole, userTier]);

  return {
    user,
    userRole,
    userTier,
    loading,
    error,
    handleLogout,
    getDashboardLink
  };
};
